import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/admin", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error: err } = await signIn(email, password);
    if (err) {
      setError("Invalid email or password. Please try again.");
    } else {
      navigate("/admin", { replace: true });
    }
    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-cool-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-loader-4-line text-2xl text-navy/40 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cool-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-8 flex items-center justify-center bg-navy rounded-md">
              <i className="ri-newspaper-line text-gold text-base" />
            </div>
            <span className="text-lg font-bold text-navy tracking-tight">EU Next Grants</span>
          </div>
          <h1 className="text-2xl font-bold text-navy mb-2">Admin Panel</h1>
          <p className="text-sm text-navy/50">Sign in to manage news and publications</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg border border-cool-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-md">
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-500 text-sm" />
                </span>
                <p className="text-xs text-red-600 font-medium">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-navy/70 uppercase tracking-wider mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full px-4 py-2.5 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-navy/70 uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full px-4 py-2.5 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap mt-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin text-base" />
                  </span>
                  Signing in…
                </>
              ) : (
                <>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-login-box-line text-base" />
                  </span>
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-navy/30 mt-6">
          &copy; {new Date().getFullYear()} EU Next Grants · Admin Area
        </p>
      </div>
    </div>
  );
}
